import { useContext, useEffect, useState } from "react";
import { Button, ButtonColor } from "components/Button";
import {
  AccountResponse,
  AccountsService,
  ApiError,
  Auth0AccountRole,
  TutorLimitedResponse,
  TutorsService,
} from "client/openapi";
import { errorText } from "components/Forms/formsApi";
import BookingProfileCard from "components/PublicBooking/BookingProfileCard";
import { OrgRolesAndAccountContext } from "util/OrgRolesAccountContext";
import { DialogActions, DialogClose, DialogContent } from "components/Dialog";
import "intl-tel-input/build/css/intlTelInput.css";
import IntlTelInput from "intl-tel-input/react";
import Notifications from "util/notifications";
import { PageStatus } from "types";
import "./index.css";
import { MdModeEditOutline } from "react-icons/md";
import { RxCross2 } from "react-icons/rx";

export default function PersonalSettings({
  account,
}: {
  account: AccountResponse;
}) {
  const [phoneNumber, setPhoneNumber] = useState(account.phone_number);
  const [smsEnabled, setSmsEnabled] = useState(account.sms_enabled);
  const [isEditingPhoneNumber, setIsEditingPhoneNumber] = useState(false);

  // A tutor edits their own booking page here; admins use the tutor page.
  const { currently_selected_role, currently_selected_organization } =
    useContext(OrgRolesAndAccountContext);
  const [tutor, setTutor] = useState<TutorLimitedResponse>();
  useEffect(() => {
    if (currently_selected_role !== Auth0AccountRole.ORG_TUTOR) return;
    TutorsService.getTutorsMe()
      .then((tutors) =>
        setTutor(
          tutors.find((t) => t.org_id === currently_selected_organization)
        )
      )
      .catch(() => setTutor(undefined));
  }, [currently_selected_role, currently_selected_organization]);

  const [error, setError] = useState<ApiError>();
  const [status, setStatus] = useState<PageStatus>(PageStatus.SUCCESS);

  const accountChanged =
    account.phone_number !== phoneNumber || account.sms_enabled !== smsEnabled;

  const updatePersonalSettings = async () => {
    setStatus(PageStatus.LOADING);
    try {
      await AccountsService.updateAccount({
        requestBody: {
          phone_number: phoneNumber,
          sms_enabled: smsEnabled,
        },
      });
      account.phone_number = phoneNumber;
      account.sms_enabled = smsEnabled;
      setStatus(PageStatus.SUCCESS);
      Notifications.success("Personal settings updated successfully");
    } catch (err) {
      setStatus(PageStatus.ERROR);
      setError(err as ApiError);
    }
  };

  useEffect(() => {
    if (error) {
      Notifications.error(
        errorText(error, "Not saved. Check your connection and try again.")
      );
    }
  }, [error]);

  const isDisabled = status === PageStatus.LOADING || !accountChanged;

  const onClose = () => {
    setStatus(PageStatus.SUCCESS);
    setError(undefined);
    setPhoneNumber(account.phone_number);
    setSmsEnabled(account.sms_enabled);
    setIsEditingPhoneNumber(false);
  };

  return (
    <DialogContent
      onClose={onClose}
      className="dialog-content dialog-content--left wide bg-neutral-50 font-montserrat pb-2 min-h-[85vh] overflow-x-hidden"
      alignLeft={true}
      gradientHeader={true}
    >
      <h1 className="title mb-6">Personal Settings</h1>
      <div className="infoItem">
        <p className="label">Email</p>
        <p>{account.email}</p>
      </div>
      <div className="infoItem">
        <p className="label">Phone</p>
        <div className="phoneInputContainer">
          <div className="flex flex-row gap-4">
            <IntlTelInput
              disabled={!isEditingPhoneNumber}
              inputProps={{
                className: `input mt-2 mb-2 ${
                  !isEditingPhoneNumber ? "opacity-70 " : ""
                }`,
                name: "studentPhone",
                defaultValue: account.phone_number?.replace(
                  /^\+\d+\s\d{3}\s/,
                  ""
                ),
              }}
              onChangeNumber={(number) => setPhoneNumber(number)}
              initOptions={{
                loadUtils: () => import("intl-tel-input/build/js/utils.js"),
                strictMode: true,
                autoPlaceholder: "aggressive",
                placeholderNumberType: "MOBILE",
                countryOrder: ["us", "gb", "ca"],
                initialCountry: "auto",
                geoIpLookup: (success, failure) => {
                  fetch("https://ipapi.co/json/")
                    .then((res) => res.json())
                    .then((data) => success(data.country_code))
                    .catch(() => failure());
                },
                separateDialCode: true,
                nationalMode: true,
              }}
            />
            <div className="mt-4">
              {isEditingPhoneNumber ? (
                <RxCross2
                  onClick={() => setIsEditingPhoneNumber(false)}
                  size={20}
                />
              ) : (
                <MdModeEditOutline
                  onClick={() => setIsEditingPhoneNumber(true)}
                  size={20}
                />
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="infoItem">
        <p className="label">Enable SMS Notifications For Meeting Booking</p>
        <div className="flex items-center">
          <input
            type="checkbox"
            className={`!border-black rounded ${
              smsEnabled ? "!bg-green-400" : "!bg-none"
            } focus:!outline-none`}
            checked={smsEnabled}
            onChange={(e) => setSmsEnabled(e.target.checked)}
          />
        </div>
      </div>
      {tutor && (
        <div className="mt-6">
          <BookingProfileCard
            tutorId={tutor.id}
            firstName={tutor.first_name}
            lastName={tutor.last_name}
            canHide={false}
          />
        </div>
      )}

      <DialogActions>
        <div className="buttons" style={{ marginTop: "30px" }}>
          <div className="actionButton">
            <Button
              color={isDisabled ? ButtonColor.GRAY : ButtonColor.GREEN}
              onClick={() => updatePersonalSettings()}
              disabled={isDisabled}
            >
              Save
            </Button>
          </div>
          <div className="actionButton">
            <DialogClose asChild>
              <Button color={ButtonColor.GRAY} onClick={onClose}>
                Cancel
              </Button>
            </DialogClose>
          </div>
        </div>
      </DialogActions>
    </DialogContent>
  );
}
